/**
 * Timbrica — AI Upscale shared pure helpers.
 *
 * Pure, side-effect-free functions used by BOTH the main-thread proxy
 * (`ai-upscale-core.js`, for the `_test` hooks) AND the inference worker
 * (`ai-upscale-worker.js`). No DOM, no ORT, no `window` — uses `self`, so the
 * same file `importScripts()`-loads in a Worker and `<script>`-loads on the
 * page. Keeping these in one place is why the fp16 math can't drift between
 * the two contexts.
 */
(function (root) {
    'use strict';

    function bytesToHex(buf) {
        var b = new Uint8Array(buf), s = '';
        for (var i = 0; i < b.length; i++) {
            var h = b[i].toString(16);
            s += h.length === 1 ? '0' + h : h;
        }
        return s;
    }

    // ─── IEEE 754 half-float (float16) <-> float32 ──────────────────────
    // ONNX Runtime Web represents float16 tensors as Uint16Array (raw 16-bit
    // halves). fp16 models (realesrgan-x4-fp16.onnx) reject float32 input with
    // `OrtRun() ERROR: Unexpected input data type` — we convert on the fly.
    var _f32 = new Float32Array(1);
    var _i32 = new Int32Array(_f32.buffer);
    function f32ToF16(val) {
        _f32[0] = val;
        var x = _i32[0];
        var bits = (x >> 16) & 0x8000;
        var m = (x >> 12) & 0x07ff;
        var e = (x >> 23) & 0xff;
        if (e < 103) return bits;
        if (e > 142) {
            bits |= 0x7c00;
            bits |= ((e === 255) ? 0 : 1) && (x & 0x007fffff);
            return bits;
        }
        if (e < 113) {
            m |= 0x0800;
            bits |= (m >> (114 - e)) + ((m >> (113 - e)) & 1);
            return bits;
        }
        bits |= ((e - 112) << 10) | (m >> 1);
        bits += m & 1;
        return bits;
    }
    function f16ToF32(h) {
        var s = (h & 0x8000) >> 15;
        var e = (h & 0x7c00) >> 10;
        var f =  h & 0x03ff;
        if (e === 0) return (s ? -1 : 1) * Math.pow(2, -14) * (f / 1024);
        if (e === 0x1f) return f ? NaN : ((s ? -1 : 1) * Infinity);
        return (s ? -1 : 1) * Math.pow(2, e - 15) * (1 + (f / 1024));
    }
    function float32ArrayToFloat16Uint16(src) {
        var dst = new Uint16Array(src.length);
        for (var i = 0; i < src.length; i++) dst[i] = f32ToF16(src[i]);
        return dst;
    }
    function float16Uint16ToFloat32Array(src) {
        var dst = new Float32Array(src.length);
        for (var i = 0; i < src.length; i++) dst[i] = f16ToF32(src[i]);
        return dst;
    }

    // Model's expected input dtype — 'float16' or 'float32'. Uses ORT
    // inputMetadata when available; falls back to float32.
    function detectModelInputDtype(session) {
        try {
            var inMeta = session.inputMetadata || session.inputs;
            if (!inMeta) return 'float32';
            var firstName = (session.inputNames && session.inputNames[0]) ||
                            (Array.isArray(inMeta) ? (inMeta[0] && inMeta[0].name) : Object.keys(inMeta)[0]);
            var meta = Array.isArray(inMeta) ? inMeta[0] : inMeta[firstName];
            if (meta && meta.type) {
                var t = String(meta.type).toLowerCase();
                if (t.indexOf('float16') !== -1 || t.indexOf('fp16') !== -1) return 'float16';
            }
        } catch (_) {}
        return 'float32';
    }

    // Decompose a frame into tile rectangles in INPUT coords, `overlap` px
    // overlap between neighbours. Last row/column may be smaller.
    function planTiles(width, height, tileSize, overlap) {
        var stride = tileSize - overlap;
        var tiles = [];
        for (var y = 0; y < height; y += stride) {
            var ty = Math.min(y, Math.max(0, height - tileSize));
            for (var x = 0; x < width; x += stride) {
                var tx = Math.min(x, Math.max(0, width - tileSize));
                tiles.push({
                    x: tx, y: ty,
                    w: Math.min(tileSize, width - tx),
                    h: Math.min(tileSize, height - ty)
                });
                if (tx + tileSize >= width) break;
            }
            if (ty + tileSize >= height) break;
        }
        return tiles;
    }

    // Cosine-taper alpha mask, cached per (size, overlap) — feathers tile
    // boundaries during stitch so seams don't show.
    var _maskCache = {};
    function makeFeatherMask(sizeOut, overlapOut) {
        var key = sizeOut + ':' + overlapOut;
        if (_maskCache[key]) return _maskCache[key];
        var m = new Float32Array(sizeOut);
        for (var i = 0; i < sizeOut; i++) {
            var distFromEdge = Math.min(i, sizeOut - 1 - i);
            if (distFromEdge >= overlapOut) {
                m[i] = 1;
            } else {
                var t = distFromEdge / overlapOut;
                m[i] = 0.5 - 0.5 * Math.cos(t * Math.PI);
            }
        }
        _maskCache[key] = m;
        return m;
    }

    /**
     * Banded tile stitcher — the same feather-blend as before, but holding
     * only a SLIDING BAND of the output instead of the whole thing.
     *
     * The old shape allocated `accum`(ow*oh*3 f32) + `weight`(ow*oh f32) +
     * the final RGBA up front = **324 bytes per INPUT pixel**. Measured in a
     * real browser: fine to 8 MP, and every allocation above ~12 MP throws
     * `RangeError: Array buffer allocation failed` — regardless of machine
     * RAM (telemetry: a 32 GB desktop failing on a 0.4 MB PNG, because a
     * small PNG can still be a huge image). A 12 MP phone photo — today's
     * default camera output — therefore failed for everyone.
     *
     * planTiles() emits tiles row-major with a non-decreasing y, so once a
     * tile row starts at output row Y, everything above Y is final and can be
     * written out and dropped. Band = tileOut + overlapOut rows.
     * Measured after this change: works to 24 MP (3× the old ceiling); the
     * remaining wall is the output RGBA itself, which no algorithm avoids.
     *
     * Tiles MUST be fed in planTiles order — feeding a tile that starts above
     * the current band throws rather than silently corrupting the image.
     */
    /**
     * Output-pixel budget for the final RGBA — mirrors photo-enhancer's
     * outputBudget() RAM ladder (deviceMemory ≤2 GB → 16 MPx, ≤4 → 40 MPx,
     * else 120 MPx). Chromium exposes navigator.deviceMemory in workers too;
     * where it is absent (Safari/Firefox) the 4 GB tier applies.
     */
    function outputPixelBudget() {
        var dm = (typeof navigator !== 'undefined' && navigator.deviceMemory) || 4;
        return dm <= 2 ? 16000000 : (dm <= 4 ? 40000000 : 120000000);
    }

    /* The literal `TOO_BIG` marker is what the tool UIs already match to their
       "image too large" copy (photo-enhancer maps /TOO_BIG/ → err_too_big);
       `.code` rides the worker's existing {type:'error', code} channel. */
    function makeTooBigError(ow, oh) {
        var err = new Error('TOO_BIG: upscale output ' + ow + 'x' + oh
            + ' exceeds this device\'s memory budget');
        err.code = 'ai_output_too_big';
        return err;
    }

    function createTileStitcher(opts) {
        var ow = opts.ow | 0, oh = opts.oh | 0;
        var bandRows = Math.max(1, Math.min(oh, opts.bandRows | 0));
        var mask = opts.maskRow;
        /* Budget BEFORE allocating: `new Uint8ClampedArray(ow*oh*4)` on an
           impossible size does not degrade — it throws `RangeError: Array
           buffer allocation failed` (Sentry FC). The try/catch stays anyway:
           heap fragmentation can fail an allocation that is UNDER budget, and
           both cases must surface as the same typed, user-explainable error
           rather than an uncaught worker crash. */
        if (ow < 1 || oh < 1 || ow * oh > outputPixelBudget()) {
            throw makeTooBigError(ow, oh);
        }
        var rgba, accum, weight;
        try {
            rgba = new Uint8ClampedArray(ow * oh * 4);
            accum = new Float32Array(ow * bandRows * 3);
            weight = new Float32Array(ow * bandRows);
        } catch (e) {
            throw makeTooBigError(ow, oh);
        }
        var bandY0 = 0;

        function flushTo(yEnd) {
            if (yEnd > oh) yEnd = oh;
            for (var y = bandY0; y < yEnd; y++) {
                var ro = (y - bandY0) * ow, ao = ro * 3, po = y * ow * 4;
                for (var x = 0; x < ow; x++) {
                    var w = weight[ro + x] || 1, a = ao + x * 3, p = po + x * 4;
                    var r = Math.round(accum[a] / w * 255);
                    var g = Math.round(accum[a + 1] / w * 255);
                    var bl = Math.round(accum[a + 2] / w * 255);
                    rgba[p]     = r < 0 ? 0 : r > 255 ? 255 : r;
                    rgba[p + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
                    rgba[p + 2] = bl < 0 ? 0 : bl > 255 ? 255 : bl;
                    rgba[p + 3] = 255;
                }
            }
        }

        function slide(newY0) {
            if (newY0 <= bandY0) return;
            flushTo(newY0);
            var shift = newY0 - bandY0;
            var keep = bandRows - shift;
            if (keep > 0) {
                accum.copyWithin(0, shift * ow * 3);
                weight.copyWithin(0, shift * ow);
                accum.fill(0, keep * ow * 3);
                weight.fill(0, keep * ow);
            } else {
                accum.fill(0);
                weight.fill(0);
            }
            bandY0 = newY0;
        }

        return {
            rgba: rgba,
            /** one model tile: its top-left in OUTPUT coords + planar RGB data */
            add: function (dstX0, dstY0, outArr, outW, outH, validW, validH, rOff, gOff, bOff) {
                if (dstY0 < bandY0) {
                    throw new Error('tile out of band order: ' + dstY0 + ' < ' + bandY0);
                }
                slide(dstY0);
                for (var py = 0; py < validH; py++) {
                    var dstY = dstY0 + py;
                    if (dstY >= oh) break;
                    var ay = mask[py] || 1;
                    var ro = (dstY - bandY0) * ow, ao = ro * 3;
                    for (var px = 0; px < validW; px++) {
                        var dstX = dstX0 + px;
                        if (dstX >= ow) break;
                        var al = ay * (mask[px] || 1);
                        var srcP = py * outW + px, a = ao + dstX * 3;
                        accum[a]     += outArr[rOff + srcP] * al;
                        accum[a + 1] += outArr[gOff + srcP] * al;
                        accum[a + 2] += outArr[bOff + srcP] * al;
                        weight[ro + dstX] += al;
                    }
                }
            },
            finish: function () { flushTo(oh); return rgba; }
        };
    }

    /* Known-answer support — box-average a planar float RGB tensor ([3][h][w],
       0..1, the model's output) back down by an integer factor into 8-bit RGBA.
       The upscaler's test question is "does the picture, averaged back to the
       input size, come back as the input?" (OrtEpVerify.psnr against the
       synthetic tile): a healthy ×4 model answers yes within a few dB of
       resampling loss; a GPU that computes the kernels wrong (Adreno 730 — half
       of every 16 columns garbage) answers no by 15+ dB, and the striped
       columns survive the averaging as a period-4 signature. Pure math, shared
       so the worker and the node test run the same code. Uint8ClampedArray
       rounds and clamps by itself (no +0.5 — it turned 128.00001 into 129 once). */
    function boxDownscalePlanarToRGBA(planar, w, h, f) {
        f = f | 0; if (f < 1) f = 1;
        var ow = Math.floor(w / f), oh = Math.floor(h / f);
        var out = new Uint8ClampedArray(ow * oh * 4);
        var plane = w * h, inv = 255 / (f * f);
        for (var y = 0; y < oh; y++) {
            for (var x = 0; x < ow; x++) {
                var r = 0, g = 0, b = 0;
                for (var dy = 0; dy < f; dy++) {
                    var row = (y * f + dy) * w + x * f;
                    for (var dx = 0; dx < f; dx++) {
                        var i = row + dx;
                        r += planar[i]; g += planar[plane + i]; b += planar[2 * plane + i];
                    }
                }
                var o = (y * ow + x) * 4;
                out[o] = r * inv; out[o + 1] = g * inv; out[o + 2] = b * inv; out[o + 3] = 255;
            }
        }
        return { data: out, width: ow, height: oh };
    }

    root.AIUpscaleShared = {
        boxDownscalePlanarToRGBA: boxDownscalePlanarToRGBA,
        createTileStitcher: createTileStitcher,
        outputPixelBudget: outputPixelBudget,
        bytesToHex: bytesToHex,
        f32ToF16: f32ToF16,
        f16ToF32: f16ToF32,
        float32ArrayToFloat16Uint16: float32ArrayToFloat16Uint16,
        float16Uint16ToFloat32Array: float16Uint16ToFloat32Array,
        detectModelInputDtype: detectModelInputDtype,
        planTiles: planTiles,
        makeFeatherMask: makeFeatherMask
    };
})(typeof self !== 'undefined' ? self : this);
